import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api.service';
import { API_ENDPOINTS } from '../config/environment';
import { FileText, Check, Loader2, AlertCircle, Shield, X, Eye, CheckCircle2, Circle } from 'lucide-react';

const LegalAgreementGate = ({ onComplete }) => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [viewing, setViewing] = useState(null);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    loadPending();
  }, []);

  const loadPending = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiService.get(API_ENDPOINTS.LEGAL_AGREEMENT.PENDING);
      if (result.success && result.data) {
        const pending = Array.isArray(result.data) ? result.data : (result.data.data || []);
        if (pending.length === 0) {
          onComplete();
          return;
        }
        setAgreements(pending);
      } else {
        onComplete();
      }
    } catch {
      onComplete();
    } finally {
      setLoading(false);
    }
  };

  const toggleAccept = (versionId) => {
    setAccepted(prev => ({ ...prev, [versionId]: !prev[versionId] }));
  };

  const allRequiredAccepted = agreements
    .filter(a => a.isRequired)
    .every(a => accepted[a.versionId]);

  const handleAccept = async () => {
    if (!allRequiredAccepted || submitting) return;
    setSubmitting(true);
    setError('');

    const versionIds = agreements
      .filter(a => accepted[a.versionId])
      .map(a => a.versionId);

    try {
      const result = await apiService.post(API_ENDPOINTS.LEGAL_AGREEMENT.BULK_ACCEPT, { versionIds });
      if (result.success) {
        onComplete();
      } else {
        setError(result.error || 'Bir hata oluştu');
      }
    } catch {
      setError('Bağlantı hatası');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="lag-overlay">
        <div className="lag-loader">
          <Loader2 size={32} className="lag-spin" />
          <span>Kontrol ediliyor...</span>
        </div>
      </div>
    );
  }

  if (agreements.length === 0) return null;

  const acceptedCount = agreements.filter(a => accepted[a.versionId]).length;

  return (
    <div className="lag-overlay">
      <div className="lag-card">
        <div className="lag-header">
          <div className="lag-header-icon">
            <Shield size={24} />
          </div>
          <div>
            <h2 className="lag-title">Sözleşme Onayı</h2>
            <p className="lag-subtitle">Devam etmek için aşağıdaki sözleşmeleri inceleyip onaylamanız gerekmektedir.</p>
            <span className="lag-counter">{acceptedCount} / {agreements.length} onaylandı</span>
          </div>
        </div>

        <div className="lag-body">
          {agreements.map((ag) => {
            const isAccepted = !!accepted[ag.versionId];

            return (
              <div key={ag.versionId} className={`lag-item ${isAccepted ? 'lag-item-accepted' : ''} ${ag.isRequired ? 'lag-item-required' : ''}`}>
                <div className="lag-item-row">
                  <button
                    className={`lag-check-btn ${isAccepted ? 'lag-checked' : ''}`}
                    onClick={() => toggleAccept(ag.versionId)}
                  >
                    {isAccepted ? <CheckCircle2 size={22} /> : <Circle size={22} />}
                  </button>
                  <div className="lag-item-info" onClick={() => toggleAccept(ag.versionId)}>
                    <span className="lag-item-title">{ag.title}</span>
                    <span className="lag-item-meta">
                      v{ag.versionNumber}
                      {ag.isRequired
                        ? <span className="lag-required">Zorunlu</span>
                        : <span className="lag-optional">İsteğe Bağlı</span>
                      }
                    </span>
                  </div>
                  <button className="lag-read-btn" onClick={() => setViewing(ag)}>
                    <Eye size={15} />
                    <span>Oku</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {error && (
          <div className="lag-error">
            <AlertCircle size={14} />
            <span>{error}</span>
          </div>
        )}

        <div className="lag-footer">
          <button
            className="lag-btn"
            disabled={!allRequiredAccepted || submitting}
            onClick={handleAccept}
          >
            {submitting ? <Loader2 size={18} className="lag-spin" /> : <Check size={18} />}
            <span>Kabul Et ve Devam Et</span>
          </button>
        </div>
      </div>

      {viewing && (
        <div className="lag-modal-overlay">
          <div className="lag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lag-modal-header">
              <div className="lag-modal-title-row">
                <FileText size={18} className="lag-item-icon" />
                <div>
                  <h3 className="lag-modal-title">{viewing.title}</h3>
                  <span className="lag-item-meta">
                    v{viewing.versionNumber}
                    {viewing.isRequired && <span className="lag-required">Zorunlu</span>}
                  </span>
                </div>
              </div>
              <button className="lag-modal-close" onClick={() => setViewing(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="lag-modal-body" dangerouslySetInnerHTML={{ __html: viewing.contentHtml }} />
            <div className="lag-modal-footer">
              {!accepted[viewing.versionId] ? (
                <button
                  className="lag-modal-accept-btn"
                  onClick={() => {
                    toggleAccept(viewing.versionId);
                    setViewing(null);
                  }}
                >
                  <CheckCircle2 size={16} />
                  <span>Okudum ve Kabul Ediyorum</span>
                </button>
              ) : (
                <button className="lag-modal-accepted-btn" onClick={() => setViewing(null)}>
                  <Check size={16} />
                  <span>Onaylandı</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalAgreementGate;
